import React, { useEffect, useState } from "react"
import {
  Col,
  Collapse,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import classNames from "classnames"
import { useTranslation } from "react-i18next"

interface AddNewCategoryProps {
  isOpen: boolean
  toggle: () => void
}

export const AddNewCategory: React.FC<AddNewCategoryProps> = ({
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("1")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("1")

  const [selectedCategory, setSelectedCategory] = useState<{
    id: string
    name: string
  } | null>(null)

  // Category options for the select dropdown
  const categoryOptions = [
    { id: "water", name: "Water" },
    { id: "soft-drinks", name: "Soft Drinks" },
    { id: "juices", name: "Juices" },
    { id: "energy-drinks", name: "Energy Drinks" },
    { id: "sports-drinks", name: "Sports Drinks" },
    { id: "dairy", name: "Dairy" },
    { id: "tea", name: "Tea" },
    { id: "coffee", name: "Coffee" },
    { id: "other", name: "Other" },
  ]

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      categoryName: null as { id: string; name: string } | null,
      categroyDescription: "",
      visibility: true,
    },
    validationSchema: Yup.object({
      categoryName: Yup.object({
        id: Yup.string().required(t("categories_categoryNameValidationMessage2") as string),
        name: Yup.string().required(t("categories_categoryNameValidationMessage2") as string),
      }).required(t("categories_categoryNameValidationMessage2") as string),
      categroyDescription: Yup.string().trim(),
      visibility: Yup.boolean(),
    }),
    onSubmit: values => {
      const { categoryName, categroyDescription, visibility } = values
      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
    setSelectedCategory(null)
  }, [isOpen])
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <p className="text-capitalize fw-semibold font-size-24">
          {t("categories_addNewCategory")}
        </p>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <div className="accordion shadow-md rounded-3" id="accordion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={classNames("accordion-button", "fw-medium", {
                    collapsed: !openFirstAccordion,
                  })}
                  type="button"
                  onClick={() =>
                    setOpenFirstAccordion(
                      openFirstAccordion === "0" ? "1" : "0"
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  {t("common_basicInfo")}
                </button>
              </h2>
              <Collapse
                isOpen={openFirstAccordion === "1"}
                className="accordion-collapse"
              >
                <div className="accordion-body">
                  <FormGroup>
                    <FormLabel htmlFor="categoryName" required>
                      {t("categories_categoryName")}
                    </FormLabel>
                    <Select
                      id="categoryName"
                      name="categoryName"
                      value={selectedCategory}
                      onChange={(option: any) => {
                        setSelectedCategory(option)
                        validation.setFieldValue("categoryName", option)
                      }}
                      options={categoryOptions}
                      getOptionLabel={(option: any) => option.name}
                      getOptionValue={(option: any) => option.id}
                      placeholder={t("categories_enterCategoryName") as string}
                      classNamePrefix="select2-selection"
                      isClearable
                      isSearchable
                    />
                    {validation.touched.categoryName && validation.errors.categoryName && (
                      <FormFeedback className="d-block">
                        {validation.errors.categoryName as string}
                      </FormFeedback>
                    )}
                  </FormGroup>
                  <FormInput
                    label={t("categories_categoryDescription") as string}
                    name="categroyDescription"
                    type="textarea"
                    placeholder={
                      t("categories_enterCategoryDescription") as string
                    }
                    validation={validation}
                    rows="3"
                  />
                </div>
              </Collapse>
            </div>
          </div>
          <div className="accordion shadow-md rounded-3 mt-4" id="accordion">
            <div className="accordion-item">
              <h2 className="accordion-header" id="headingOne">
                <button
                  className={classNames("accordion-button", "fw-medium", {
                    collapsed: !openSecondAccordion,
                  })}
                  type="button"
                  onClick={() =>
                    setOpenSecondAccordion(
                      openSecondAccordion === "0" ? "1" : "0"
                    )
                  }
                >
                  {t("common_visibility")}
                </button>
              </h2>
              <Collapse
                isOpen={openSecondAccordion === "1"}
                className="accordion-collapse"
              >
                <div className="accordion-body">
                  <FormGroup className="d-flex align-items-center justify-content-between gap-3">
                    <FormLabel htmlFor="visibility">
                      <span className="fw-bold">
                        {t("categories_changeVisibility")}
                      </span>{" "}
                      ({t("categories_categoryVisibility")})
                    </FormLabel>
                    <div className="form-check form-switch mb-2">
                      <input
                        name="visibility"
                        type="checkbox"
                        className="form-check-input"
                        id="visibility"
                        checked={validation.values.visibility}
                        onClick={e => {
                          e.stopPropagation()
                          validation.setFieldValue(
                            "visibility",
                            !validation.values.visibility
                          )
                        }}
                        onChange={e => {
                          e.stopPropagation()
                        }}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </FormGroup>
                </div>
              </Collapse>
            </div>
          </div>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("categories_createCategory")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
